import { Analytics } from '@cobliteam/analytics';
import { Location } from '@cobliteam/common-types';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags/useFeatureFlags';
import Button from '@cobliteam/ui/components/Button';
import Icon from '@cobliteam/ui/icons';
import { useHistory } from 'react-router-dom';
import AIIcon from '../components/AIIcon';
import { useSchedulingAgent } from '../store';

const CTA_COLOR = '#0234E1';

const JOURNEY_LOCATIONS: Record<string, string> = {
  'driver-registration': Location.DRIVER_MANAGEMENT,
  'vehicle-groups': Location.VEHICLES_GROUPS,
  installation: Location.ORDERS,
  checklists: Location.CHECKLIST_ALL,
  'fleet-policies': Location.ALERTS_TRIGGERS_CREATE,
  'locais-de-interesse': Location.GEOFENCES_CREATE,
};

interface UseAiRecommendationCtaOptions {
  component: string;
  journeyId: string;
  aiPrompt?: string;
  /** Label shown on the AI CTA button. Defaults to 'Ajuda com a Cobli AI'. */
  label?: string;
  /** Custom action when AI CTA is clicked. Overrides default navigation to /ai. */
  action?: () => void;
  /** Label shown for the CTA. Defaults to 'Ir para a página'. */
  fallbackLabel?: string;
  /** Action called when the CTA is clicked. Defaults to navigating to the journey page. */
  fallbackAction?: () => void;
}

const useAiRecommendationCta = ({ component, journeyId, aiPrompt, label, action, fallbackLabel, fallbackAction }: UseAiRecommendationCtaOptions) => {
  const history = useHistory();
  const { aiChat } = useFeatureFlags();
  const openSchedulingAgent = useSchedulingAgent((s) => s.open);

  const isInstallation = journeyId === 'installation';
  const showAiCta = aiChat || isInstallation;

  const effectiveLabel = label ?? (isInstallation ? 'Agendar com IA' : 'Ajuda com a Cobli AI');

  const onCta = () => {
    Analytics.track('Ai Recommendation call to action clicked', {
      component: component,
      journeyId: journeyId,
    });

    if (isInstallation) {
      Analytics.track('Scheduling Agent opened', { component: 'ai-recommendation-cta', journeyId });
      openSchedulingAgent('Quero agendar minhas instalações pendentes');
      return;
    }

    if (action && aiChat) {
      action();
    } else if (aiChat) {
      history.push(`${Location.AI}?prompt=${encodeURIComponent(aiPrompt ?? 'Como posso maximizar o valor da minha frota com a plataforma Cobli?')}`);
    } else if (fallbackAction) {
      fallbackAction();
    } else {
      const location = JOURNEY_LOCATIONS[journeyId];
      if (location) history.push(location);
    }
  };

  const ctaButton = showAiCta ? (
    <button
      type='button'
      onClick={onCta}
      className='flex items-center gap-2 rounded-xl px-4 py-2 font-semibold text-sm text-white transition-opacity hover:opacity-90'
      style={{ background: CTA_COLOR }}
    >
      {!isInstallation && <AIIcon size={22} sparklesPaint='white' />}
      {effectiveLabel}
      {isInstallation && <Icon name='sparkles' className='text-white' />}
    </button>
  ) : (
    <Button variant='contained' color='primary' onClick={onCta}>
      {fallbackLabel ?? 'Ir para a página'}
    </Button>
  );

  return { ctaButton };
};

export default useAiRecommendationCta;
